import React from 'react';
import { CONCEITOS, COR_DO_CONCEITO } from '../utils/habilidades';

/**
 * Blocos visuais compartilhados pelos painéis do diário (rede e professor).
 *
 * Todos os dados vêm de /dashboard-diario/ — frequência, notas e aulas que
 * os professores já lançam. As cores de status (verde/âmbar/vermelho) sempre
 * acompanham rótulo direto, para não depender só da cor.
 */

export const BRAND = {
  accent: '#0974BA',
  accent2: '#2E4974',
  good: '#13983D',
  warn: '#F9B047',
  crit: '#E83A3C',
  purple: '#7C3AED',
};

const TONE = {
  accent: BRAND.accent,
  nav: BRAND.accent2,
  good: BRAND.good,
  warn: BRAND.warn,
  crit: BRAND.crit,
};

/** Percentual formatado; null vira '—'. */
export function pct(value) {
  return value === null || value === undefined ? '—' : `${fmt(value)}%`;
}

/** Número pt-BR com vírgula; null vira '—'. */
export function fmt(value, dashIfNull = false) {
  if (value === null || value === undefined) return dashIfNull ? '—' : '0';
  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

export function rateColor(value) {
  if (value === null || value === undefined) return 'text-slate-400';
  if (value >= 90) return 'text-[#13983D]';
  if (value >= 75) return 'text-[#F9B047]';
  return 'text-[#E83A3C]';
}

/** Média de 0 a 10: >= 7,0 aprova (verde), >= 5,0 recuperação (âmbar), abaixo é crítico. */
export const NOTA_APROVACAO = 7;
export const NOTA_CRITICA = 5;
export function mediaColor(value) {
  if (value === null || value === undefined) return 'text-slate-400';
  if (value >= NOTA_APROVACAO) return 'text-[#13983D]';
  if (value >= NOTA_CRITICA) return 'text-[#F9B047]';
  return 'text-[#E83A3C]';
}

const MESES = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
export function monthLabel(ym) {
  const m = Number((ym || '').split('-')[1]);
  return MESES[m] || ym;
}

export function cardCls(darkMode) {
  return darkMode
    ? 'bg-slate-800/50 border border-slate-700/50'
    : 'bg-white border border-slate-200 shadow-sm';
}

/** Cartão de indicador (KPI) com faixa colorida à esquerda. */
export function KpiCard({ label, value, unit, foot, tone = 'accent', darkMode, onClick }) {
  const color = TONE[tone] || BRAND.accent;
  // Com onClick o cartão vira botão (ex.: leva à lista por trás do número).
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl p-4 ${cardCls(darkMode)} ${onClick
        ? `text-left w-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`
        : ''}`}
    >
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: color }} />
      <div className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</div>
      <div className={`mt-2 text-3xl font-semibold tabular-nums ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
        {value}
        {unit && <span className={`ml-0.5 text-base font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{unit}</span>}
      </div>
      {foot && <div className={`mt-1 text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{foot}</div>}
    </Tag>
  );
}

/** Barras verticais (ex.: aulas registradas por mês). */
export function MonthlyBars({ data = [], darkMode, unit = '' }) {
  const max = Math.max(1, ...data.map(d => d.count || 0));
  return (
    <div className="flex items-end gap-1 sm:gap-1.5 h-36 pt-2 min-w-0">
      {data.map((d) => (
        <div key={d.month} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full group" title={`${monthLabel(d.month)}: ${d.count} aula(s)`}>
          <span className={`text-[10px] tabular-nums ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{d.count || 0}</span>
          <div
            className="w-full max-w-[26px] rounded-t transition-all group-hover:brightness-110"
            style={{ height: `${((d.count || 0) / max) * 100}%`, minHeight: d.count ? 4 : 0, background: BRAND.accent }}
          />
          <span className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{monthLabel(d.month)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Barras horizontais ranqueadas. `rows`: [{ label, value, sub }].
 * `mode='freq'` colore por faixa (>=90 verde, >=75 âmbar, <75 vermelho) e
 * desenha a linha de piso em 75%. `mode='nota'` (escala 0–10) faz o mesmo com
 * a média: >=7 verde, >=5 âmbar, <5 vermelho, e a linha na aprovação (7,0).
 *
 * A barra nunca passa de 100% do trilho. Um valor acima da escala (uma média
 * lançada acima de 10, por exemplo) chegava a cobrir a coluna do número e
 * escondia o primeiro dígito: "14,9" virava "4,9" e a linha parecia maior do
 * que outra de 7,8 — um "erro" do gráfico que era, na verdade, nota fora da
 * escala. Agora a barra enche o trilho e o valor ganha o aviso `fora da
 * escala`, que é a informação que importa para quem lê o painel.
 *
 * No celular o rótulo sobe para cima da barra: a linha de 150px + barra +
 * número não cabia em 390px e empurrava a página para fora da tela.
 *
 * A partir de sm as linhas dividem uma grade só, e a coluna do rótulo cresce
 * até o maior nome: com 150px fixos, "EMEIF PRESIDENTE JOÃO PESSOA" virava
 * "EMEIF PRESIDENTE JOÃ…". Quem encolhe é a barra, até um mínimo de 120px.
 */
export function RankedBars({ rows = [], darkMode, mode = 'freq', suffix = '%', max = 100 }) {
  const colorFor = (v) => {
    if (mode !== 'freq' && mode !== 'nota') return BRAND.accent;
    if (v === null || v === undefined) return darkMode ? '#475569' : '#cbd5e1';
    if (mode === 'nota') {
      if (v >= NOTA_APROVACAO) return BRAND.good;
      if (v >= NOTA_CRITICA) return BRAND.warn;
      return BRAND.crit;
    }
    if (v >= 90) return BRAND.good;
    if (v >= 75) return BRAND.warn;
    return BRAND.crit;
  };
  const linha = mode === 'freq'
    ? { left: '75%', title: 'Piso de 75%' }
    : mode === 'nota'
    ? { left: `${(NOTA_APROVACAO / max) * 100}%`, title: 'Aprovação: 7,0' }
    : null;
  if (!rows.length) {
    return <div className={`text-sm py-6 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sem dados no período.</div>;
  }
  return (
    <div
      className="flex flex-col gap-3 sm:grid sm:items-center sm:gap-x-2.5 sm:gap-y-2.5"
      style={{ gridTemplateColumns: 'minmax(0, max-content) minmax(120px, 1fr) 56px' }}
    >
      {rows.map((r, i) => {
        const temValor = r.value !== null && r.value !== undefined;
        const foraDaEscala = temValor && r.value > max;
        const largura = Math.min(100, Math.max(0, ((r.value || 0) / max) * 100));
        const texto = temValor ? `${fmt(r.value)}${suffix}` : '—';
        const valorCls = `text-xs font-semibold tabular-nums ${
          foraDaEscala ? 'text-[#E83A3C]' : (darkMode ? 'text-slate-200' : 'text-slate-700')
        }`;
        return (
          <div key={i} className="flex flex-col gap-1 sm:contents">
            <div className="flex items-baseline justify-between gap-2 min-w-0">
              <div className={`text-xs truncate ${darkMode ? 'text-slate-300' : 'text-slate-700'}`} title={r.label}>
                {r.label}
                {r.sub && <span className={`ml-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>· {r.sub}</span>}
              </div>
              {/* No celular o número acompanha o rótulo; a partir de sm ele
                  volta para a coluna da direita. */}
              <span className={`${valorCls} sm:hidden whitespace-nowrap`}>{texto}</span>
            </div>
            <div
              className={`relative h-4 rounded overflow-hidden ${darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}`}
              title={foraDaEscala ? `${texto} — acima da escala de 0 a ${max}` : undefined}
            >
              <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${largura}%`, background: colorFor(r.value) }} />
              {linha && (
                <div className="absolute inset-y-0" style={{ left: linha.left, width: 2, background: BRAND.crit, opacity: 0.6 }} title={linha.title} />
              )}
              {/* Hachura na ponta: a barra bateu no fim do trilho porque o
                  valor estourou a escala, não porque chegou ao topo dela. */}
              {foraDaEscala && (
                <div
                  className="absolute inset-y-0 right-0 w-3"
                  style={{ background: `repeating-linear-gradient(45deg, ${BRAND.crit}, ${BRAND.crit} 2px, transparent 2px, transparent 4px)` }}
                />
              )}
            </div>
            <div className={`hidden sm:block ${valorCls} text-right`}>{texto}</div>
            {foraDaEscala && (
              <div className="text-[10px] text-[#E83A3C] sm:col-start-2 sm:col-span-2">
                acima da escala de 0 a {max} — confira o lançamento
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Distribuição das notas em 3 faixas, com rótulo em cada. */
export function NotasDistribution({ notas, darkMode }) {
  if (!notas || !notas.total) {
    return <div className={`text-sm py-6 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma nota lançada ainda.</div>;
  }
  const faixas = [
    { key: 'aprovado', label: '≥ 7,0', desc: 'Aprovado', color: BRAND.good, ...notas.aprovado },
    { key: 'recuperacao', label: '5,0–6,9', desc: 'Recuperação', color: BRAND.warn, ...notas.recuperacao },
    { key: 'critico', label: '< 5,0', desc: 'Crítico', color: BRAND.crit, ...notas.critico },
  ];
  return (
    <div className="flex flex-col gap-3 mt-1">
      {faixas.map((f) => (
        <div
          key={f.key}
          className="flex flex-col gap-1 sm:grid sm:items-center sm:gap-3"
          style={{ gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr) 96px' }}
        >
          <div className={`text-xs font-semibold flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: f.color }} />
            {f.label}
            <span className={`ml-auto font-normal sm:hidden ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <b className={darkMode ? 'text-slate-100' : 'text-slate-800'}>{fmt(f.pct)}%</b> · {f.desc}
            </span>
          </div>
          <div className={`h-5 rounded overflow-hidden ${darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}`}>
            <div className="h-full rounded" style={{ width: `${Math.min(100, Math.max(0, f.pct || 0))}%`, background: f.color }} />
          </div>
          <div className={`hidden sm:block text-xs text-right ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <b className={darkMode ? 'text-slate-100' : 'text-slate-800'}>{fmt(f.pct)}%</b> · {f.desc}
          </div>
        </div>
      ))}
    </div>
  );
}

const NOME_DO_CONCEITO = {
  HND: 'Não desenvolvida',
  HDI: 'Desenv. inicial',
  HD: 'Em desenvolvimento',
  HC: 'Consolidada',
};

/**
 * Conceitos do Registro de Habilidades (1º e 2º ano e Educação Infantil) em
 * 4 faixas — o
 * equivalente de NotasDistribution para as séries que não lançam nota.
 * `habilidades`: { total, HND: { count, pct }, HDI, HD, HC }.
 */
export function HabilidadesDistribution({ habilidades, darkMode }) {
  if (!habilidades || !habilidades.total) {
    return <div className={`text-sm py-6 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhum conceito lançado ainda.</div>;
  }
  return (
    <div className="flex flex-col gap-3 mt-1">
      {CONCEITOS.map((c) => {
        const f = habilidades[c] || { count: 0, pct: 0 };
        return (
          <div
            key={c}
            className="flex flex-col gap-1 sm:grid sm:items-center sm:gap-3"
            style={{ gridTemplateColumns: 'minmax(0, 190px) minmax(0, 1fr) 72px' }}
          >
            <div className={`text-xs font-semibold flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: COR_DO_CONCEITO[c] }} />
              {c}
              <span className={`font-normal truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{NOME_DO_CONCEITO[c]}</span>
              <b className={`ml-auto sm:hidden ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{fmt(f.pct)}%</b>
            </div>
            <div className={`h-5 rounded overflow-hidden ${darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}`}>
              <div className="h-full rounded" style={{ width: `${Math.min(100, Math.max(0, f.pct || 0))}%`, background: COR_DO_CONCEITO[c] }} />
            </div>
            <div className={`hidden sm:block text-xs text-right ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <b className={darkMode ? 'text-slate-100' : 'text-slate-800'}>{fmt(f.pct)}%</b> · {f.count}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Barra empilhada com a proporção de cada conceito (HND → HC, da esquerda
 * para a direita). O título traz as contagens, para não depender da cor.
 */
export function ConceitosBar({ contagem, darkMode, className = 'h-3' }) {
  const total = contagem?.total || 0;
  const trilho = darkMode ? 'bg-slate-700/60' : 'bg-slate-100';
  if (!total) return <div className={`${className} rounded ${trilho}`} />;
  const titulo = CONCEITOS.map((c) => `${c}: ${contagem[c]?.count || 0}`).join(' · ');
  return (
    <div className={`${className} flex rounded overflow-hidden ${trilho}`} title={titulo} role="img" aria-label={titulo}>
      {CONCEITOS.map((c) => {
        const n = contagem[c]?.count || 0;
        return n ? <div key={c} style={{ width: `${(n / total) * 100}%`, background: COR_DO_CONCEITO[c] }} /> : null;
      })}
    </div>
  );
}

/**
 * Uma ConceitosBar por linha, com rótulo e % em HC (por bimestre, por
 * componente, por série). Rótulo em cima e barra embaixo, em qualquer
 * largura: o cartão vive tanto em grade de 2 quanto de 3 colunas, e a
 * barra ao lado do rótulo ficava fina demais para ler as cores.
 */
export function ConceitosRows({ rows = [], darkMode, empty = 'Sem conceitos no período.' }) {
  const comDados = rows.filter((r) => r.contagem?.total);
  if (!comDados.length) {
    return <div className={`text-sm py-6 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{empty}</div>;
  }
  return (
    <div className="flex flex-col gap-3">
      {rows.map((r) => (
        <div key={r.label} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span className={`text-xs truncate ${darkMode ? 'text-slate-300' : 'text-slate-700'}`} title={r.label}>{r.label}</span>
            <span className={`text-xs tabular-nums whitespace-nowrap ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {r.contagem?.total ? <><b className={darkMode ? 'text-slate-100' : 'text-slate-800'}>{fmt(r.contagem.HC?.pct)}%</b> HC</> : '—'}
            </span>
          </div>
          <ConceitosBar contagem={r.contagem} darkMode={darkMode} className="h-3.5" />
        </div>
      ))}
    </div>
  );
}

/** Legenda das cores dos conceitos. */
export function ConceitosLegenda({ darkMode }) {
  return (
    <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-4 text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
      {CONCEITOS.map((c) => (
        <span key={c} className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COR_DO_CONCEITO[c] }} />
          <b className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{c}</b> {NOME_DO_CONCEITO[c].toLowerCase()}
        </span>
      ))}
    </div>
  );
}

const SIT = {
  ok: { label: 'Ok', cls: 'bg-[#13983D]/10 text-[#13983D]' },
  atencao: { label: 'Atenção', cls: 'bg-[#F9B047]/15 text-[#B87608]' },
  critico: { label: 'Crítico', cls: 'bg-[#E83A3C]/10 text-[#E83A3C]' },
};
export function SituacaoPill({ situacao }) {
  const s = SIT[situacao] || SIT.ok;
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

/** Cabeçalho de seção com título e subtítulo. */
export function SectionCard({ title, subtitle, children, darkMode, className = '', action }) {
  const titulo = <h3 className={`font-semibold text-[15px] ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{title}</h3>;
  return (
    <div className={`min-w-0 rounded-2xl p-5 ${cardCls(darkMode)} ${className}`}>
      {/* `action`: um controle à direita do título (filtro, seletor). */}
      {action ? (
        <div className="flex flex-wrap items-start justify-between gap-2">{titulo}{action}</div>
      ) : titulo}
      {subtitle && <p className={`text-xs mt-0.5 mb-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{subtitle}</p>}
      {children}
    </div>
  );
}
